import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { usePopupQueue } from '../hooks/usePopupQueue';
import { PopupStorageService } from '../services/PopupStorageService';
import { PopupButtonStrategy } from '../strategies/PopupButtonStrategy';

/**
 * GlobalPopup - Displays popup notifications
 * 
 * Refactored to follow SOLID principles:
 * - Single Responsibility: Only handles UI rendering
 * - Dependency Inversion: Uses injected services
 * - Open/Closed: Button configuration is externalized
 * 
 * @param {Array} notifications - Array of notification objects
 * @param {Object} actions - Action handlers from context
 * @param {Object} storageService - Storage service (default: PopupStorageService)
 */
export const GlobalPopup = ({
    notifications,
    actions,
    storageService = PopupStorageService
}) => {
    const { currentPopup, show, setShow, markAsProcessed } = usePopupQueue(
        notifications,
        storageService
    );

    const handleClose = () => {
        if (!currentPopup) return;

        markAsProcessed(currentPopup.id);

        // Logic based on frequency
        if (currentPopup.datos.frecuencia === 1) {
            // Session based: mark in storage
            storageService.markAsSeen(currentPopup.id);
            setShow(false);
        } else {
            // Permanent: Call API to mark as read
            if (actions && actions.handleMarkAsRead) {
                actions.handleMarkAsRead(currentPopup);
            }
            setShow(false);
        }
    };

    const handleAccept = () => {
        if (!currentPopup) return;

        markAsProcessed(currentPopup.id);

        // Check frequency setting
        if (currentPopup.datos.frecuencia === 1) {
            // Session-based: only save in sessionStorage
            storageService.markAsSeen(currentPopup.id);
        } else {
            // Permanent: save in database via API
            if (actions && actions.handleAccept) {
                actions.handleAccept(currentPopup);
            }
        }

        setShow(false);
    };

    if (!currentPopup) return null;

    const isBlocking = currentPopup.datos.requiereAceptacion;
    const buttons = PopupButtonStrategy.getButtons(currentPopup);

    // Map button actions to handlers
    const handlers = {
        close: handleClose,
        accept: handleAccept
    };

    return (
        <Modal
            show={show}
            onHide={isBlocking ? undefined : handleClose}
            backdrop={isBlocking ? 'static' : true}
            keyboard={!isBlocking}
            centered
            size="lg"
        >
            <Modal.Header closeButton={!isBlocking}>
                <Modal.Title>{currentPopup.datos.titulonotificacion}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div dangerouslySetInnerHTML={{ __html: currentPopup.datos.descripcion }} />
                {currentPopup.datos.imagenbanner && currentPopup.datos.imagenbanner.trim() && (
                    currentPopup.datos.link ? (
                        <a href={currentPopup.datos.link} target="_blank" rel="noopener noreferrer">
                            <img
                                src={currentPopup.datos.imagenbanner}
                                alt="Banner"
                                className="img-fluid mt-3"
                                style={{ cursor: 'pointer' }}
                            />
                        </a>
                    ) : (
                        <img src={currentPopup.datos.imagenbanner} alt="Banner" className="img-fluid mt-3" />
                    )
                )}
            </Modal.Body>
            <Modal.Footer>
                {buttons.map((button, index) => (
                    <Button
                        key={index}
                        variant={button.variant}
                        onClick={handlers[button.action]}
                    >
                        {button.label}
                    </Button>
                ))}
            </Modal.Footer>
        </Modal>
    );
};
